import { Node, mergeAttributes } from '@tiptap/core';
import { SiteMetaData } from '../../features/BlockEditor/types.jsx';
import i18next from 'i18next';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkPreview: {
      setLinkPreview: (src?: string, metadata?: SiteMetaData) => ReturnType;
    };
  }
}

export const LinkPreview = Node.create({
  name: 'linkPreview',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      href: {
        default: ''
      },
      title: { default: '' },
      description: {
        default: ''
      },
      image: {
        default: ''
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-link-preview]'
      }
    ];
  },

  renderHTML({ node }) {
    const hasValidImage =
      node.attrs.image && node.attrs.image !== '/png/placeholder.png';

    const padding = hasValidImage ? 'px-4' : 'pl-6 pr-4';

    return [
      'a',
      mergeAttributes({
        'data-link-preview': '',
        href: node.attrs.href,
        target: '_blank',
        rel: 'noopener noreferrer',
        dir: i18next.dir(),
        class: `relative flex w-full ${padding} py-3 flex-col items-start gap-6 rounded bg-[#F1F1F1] border-l-0 transition-all duration-200 ease-in-out box-border mb-4 hover:bg-[#F9F9F9] hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_-4px_6px_-1px_rgba(0,0,0,0.06),4px_0_6px_-1px_rgba(0,0,0,0.06)] focus:bg-[#F9F9F9] focus:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_-4px_6px_-1px_rgba(0,0,0,0.06),4px_0_6px_-1px_rgba(0,0,0,0.06)] before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:rounded-l-md before:bg-[#01ABD9]`
      }),
      [
        'div',
        {
          class: hasValidImage
            ? 'flex items-start w-full gap-3'
            : 'flex items-start w-full'
        },
        hasValidImage
          ? [
              'img',
              {
                class:
                  'flex justify-center items-center rounded-md !w-20 !h-20 aspect-square flex-shrink-0 sm:rounded md:rounded-md lg:!w-[84px] lg:!h-[84px] lg:rounded xl:rounded-md',
                src: node.attrs.image
              }
            ]
          : '',
        [
          'div',
          {
            class: 'flex flex-col items-start'
          },
          [
            'span',
            {
              class:
                'line-clamp-1 overflow-hidden text-[#1F2121] text-ellipsis font-[var(--font-notosans),Arial,sans-serif] text-xs font-bold leading-normal uppercase sm:text-xs sm:font-bold md:text-base md:font-medium md:leading-[15px] lg:text-base xl:text-base',
              'data-link-preview-related': '',
              'data-testid': 'link-preview-related'
            },
            i18next.t('related', 'RELATED')
          ],
          [
            'strong',
            {
              class:
                'text-[#1F2121] font-[var(--font-playfair),Georgia,serif] text-base font-extrabold leading-[26px] underline decoration-solid line-clamp-2 overflow-hidden text-ellipsis sm:text-lg sm:leading-[31px] md:text-lg md:leading-[28px] lg:text-xl lg:leading-[31px] xl:text-xl'
            },
            node.attrs.title
          ]
        ]
      ]
    ];
  },

  addCommands() {
    return {
      setLinkPreview:
        (src, metadata) =>
        ({ commands, state }) => {
          // Don't use placeholder image
          const imageUrl = metadata?.images?.[0] || '';

          return commands.insertContent({
            type: 'linkPreview',
            attrs: {
              href: src,
              title: metadata?.title,
              description: metadata?.description,
              image: imageUrl
            }
          });
        }
    };
  }
});
